/**
 * Doubloon Product Personalizer
 *
 * Manages the inline personalizer UI: text input, font picker, cerakote
 * selector, price breakdown, and variant resolution. Syncs hidden form
 * inputs (line item properties) with the main product form.
 *
 * Subscribes to Dawn's variant-change pub/sub to stay in sync when
 * the customer changes material/loss-protection options.
 */

if (!customElements.get('doubloon-personalizer')) {
  class DoubloonPersonalizer extends HTMLElement {
    constructor() {
      super();
      this.sectionId = this.dataset.sectionId;
      this.formId = this.dataset.productFormId;
      this.isOpen = false;
      this.confirmed = false;

      // State
      this.engravingText = '';
      this.selectedFont = 'Arial';
      this.cerakoteColor = '';
      this.customColorText = '';
      this.currentBaseVariant = null;
      this.isApplied = false;

      // Pricing (in cents)
      this.ENGRAVING_SURCHARGE = 2500;
      this.CERAKOTE_SURCHARGE = 3500;
      this.TOTAL_TEXT_CERAKOTE = 6000;

      // Whether this product has personalization variants set up
      this.hasPersonalizationVariants = false;

      // Preview image is resolved server-side in Liquid (tag-based detection)
      // No client-side material detection needed — base material is per-product, not per-variant
    }

    connectedCallback() {
      this.cacheElements();
      this.parseVariants();
      this.bindEvents();
      this.subscribeToVariantChanges();
    }

    disconnectedCallback() {
      // Remove event listeners
      this.toggleBtn?.removeEventListener('click', this._onToggle);
      this.textInput?.removeEventListener('input', this._onTextInput);
      this.textInput?.removeEventListener('focus', this._onInputFocus);
      this.fontRadios.forEach((radio) => {
        radio.removeEventListener('change', this._onFontChange);
      });
      this.cerakoteSelect?.removeEventListener('change', this._onCerakoteChange);
      this.cerakoteSelect?.removeEventListener('focus', this._onInputFocus);
      this.customColorTextField?.removeEventListener('input', this._onCustomColorInput);
      this.customColorTextField?.removeEventListener('focus', this._onInputFocus);
      this.confirmCheckbox?.removeEventListener('change', this._onConfirmChange);
      this.applyBtn?.removeEventListener('click', this._onApplyClick);

      // Unsubscribe from variant-change pub/sub
      this._unsubVariant?.();
    }

    cacheElements() {
      // Toggle
      this.toggleBtn = this.querySelector('.doubloon-personalizer__toggle');
      this.panel = this.querySelector('.doubloon-personalizer__panel');

      // Text input
      this.textInput = this.querySelector('[data-engraving-text]');
      this.charCount = this.querySelector('[data-char-count]');

      // Preview
      this.previewText = this.querySelector('[data-preview-text]');

      // Font radios
      this.fontRadios = this.querySelectorAll('[data-font-radio]');

      // Cerakote
      this.cerakoteSelect = this.querySelector('[data-cerakote-select]');
      this.customColorInput = this.querySelector('[data-custom-color-input]');
      this.customColorTextField = this.querySelector('[data-custom-color-text]');

      // Price breakdown
      this.priceBase = this.querySelector('[data-price-base]');
      this.priceEngravingRow = this.querySelector('[data-price-engraving-row]');
      this.priceCerakoteRow = this.querySelector('[data-price-cerakote-row]');
      this.priceCerakoteName = this.querySelector('[data-price-cerakote-name]');
      this.priceTotal = this.querySelector('[data-price-total]');

      // Confirmation
      this.confirmCheckbox = this.querySelector('[data-confirm-checkbox]');

      // Apply button + saved message
      this.applyBtn = this.querySelector('[data-apply-btn]');
      this.savedMessage = this.querySelector('[data-saved-message]');

      // Hidden form inputs
      this.propText = this.querySelector('[data-prop-text]');
      this.propFont = this.querySelector('[data-prop-font]');
      this.propCerakote = this.querySelector('[data-prop-cerakote]');
    }

    parseVariants() {
      const script = this.querySelector('[data-personalizer-variants]');
      if (script) {
        try {
          this.allVariants = JSON.parse(script.textContent);
        } catch (e) {
          this.allVariants = [];
        }
      }

      // Check if personalization variants exist
      this.hasPersonalizationVariants = this.allVariants.some(
        (v) => (v.title || '').includes('Text Engraving')
      );

      // Find the currently selected variant from the main form
      const productInfo = document.getElementById(`MainProduct-${this.sectionId}`);
      if (productInfo) {
        const variantInput = productInfo.querySelector('.product-variant-id');
        if (variantInput) {
          const currentId = parseInt(variantInput.value);
          this.currentBaseVariant = this.allVariants.find((v) => v.id === currentId);
        }
      }

      // If no personalization variants, show a notice on the toggle
      if (!this.hasPersonalizationVariants) {
        this.classList.add('doubloon-personalizer--unavailable');
      }
    }

    bindEvents() {
      // Store bound handler references for cleanup in disconnectedCallback
      this._onToggle = () => this.toggle();
      this._onTextInput = () => this.onTextChange();
      this._onFontChange = (e) => this.onFontChange(e.target.value);
      this._onCerakoteChange = () => this.onCerakoteChange(this.cerakoteSelect.value);
      this._onCustomColorInput = () => {
        this.customColorText = this.customColorTextField.value;
        this.syncFormInputs();
      };
      this._onConfirmChange = () => {
        this.confirmed = this.confirmCheckbox.checked;
        this.updateApplyButton();
      };
      this._onApplyClick = () => this.applyPersonalization();

      // Mobile keyboard: scroll input into view after iOS keyboard opens
      this._onInputFocus = (e) => {
        if (window.innerWidth >= 750) return;
        setTimeout(() => {
          e.target.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }, 300); // Delay lets iOS keyboard finish animating
      };

      this.toggleBtn?.addEventListener('click', this._onToggle);
      this.textInput?.addEventListener('input', this._onTextInput);
      this.textInput?.addEventListener('focus', this._onInputFocus);
      this.fontRadios.forEach((radio) => {
        radio.addEventListener('change', this._onFontChange);
      });
      this.cerakoteSelect?.addEventListener('change', this._onCerakoteChange);
      this.cerakoteSelect?.addEventListener('focus', this._onInputFocus);
      this.customColorTextField?.addEventListener('input', this._onCustomColorInput);
      this.customColorTextField?.addEventListener('focus', this._onInputFocus);
      this.confirmCheckbox?.addEventListener('change', this._onConfirmChange);
      this.applyBtn?.addEventListener('click', this._onApplyClick);
    }

    subscribeToVariantChanges() {
      if (typeof subscribe !== 'function') return;

      this._unsubVariant = subscribe('variant-change', (event) => {
        if (!event.data?.variant) return;
        this.currentBaseVariant = event.data.variant;
        this.updatePriceBreakdown();

        // If personalizer is open, resolve the correct personalization variant
        if (this.isOpen) {
          this.resolvePersonalizationVariant();
        }
      });
    }

    // ── Toggle ────────────────────────────────────────────────

    toggle() {
      // Don't open if personalization variants aren't set up
      if (!this.hasPersonalizationVariants) return;

      this.isOpen = !this.isOpen;
      this.toggleBtn.setAttribute('aria-expanded', this.isOpen);
      this.panel.hidden = !this.isOpen;

      if (this.isOpen) {
        this.classList.add('is-open');
        this.resolvePersonalizationVariant();
        this.updateApplyButton();
        this.updateSubmitButton();

        // Analytics: personalizer opened
        if (window.__doubloon_track) {
          window.__doubloon_track('Personalizer Opened', {
            product_title: this.getProductTitle(),
            product_id: this.getProductId(),
            product_url: window.location.href,
          });
        }
      } else {
        this.classList.remove('is-open');
        // Only clear personalization if it hasn't been applied
        if (!this.isApplied) {
          this.clearPersonalization();
        }
      }
    }

    clearPersonalization() {
      // Reset state
      this.engravingText = '';
      this.selectedFont = 'Arial';
      this.cerakoteColor = '';
      this.confirmed = false;
      this.isApplied = false;

      if (this.textInput) this.textInput.value = '';
      if (this.charCount) this.charCount.textContent = '0 / 45 characters';
      if (this.previewText) this.previewText.textContent = '';
      if (this.cerakoteSelect) this.cerakoteSelect.value = '';
      if (this.confirmCheckbox) this.confirmCheckbox.checked = false;
      if (this.savedMessage) this.savedMessage.hidden = true;
      if (this.applyBtn) this.applyBtn.disabled = true;

      // Reset font to Arial
      this.fontRadios.forEach((r) => {
        r.checked = r.value === 'Arial';
      });

      // Clear hidden form inputs
      this.syncFormInputs();

      // Revert to base variant (no personalization) via Dawn's native system
      this.selectPersonalizationOption('No Personalization');

      // Re-enable submit button (Dawn's own logic takes over)
      this.restoreSubmitButton();

      // Update toggle text
      this.updateToggleText();
    }

    applyPersonalization() {
      // Validate before applying
      if (!this.engravingText.trim() || !this.confirmed) return;

      // Mark as applied
      this.isApplied = true;

      // Show saved message
      if (this.savedMessage) {
        this.savedMessage.hidden = false;
      }

      // Disable apply button
      if (this.applyBtn) {
        this.applyBtn.disabled = true;
      }

      // Enable main Add to Cart button
      this.updateSubmitButton();

      // Update toggle button text
      this.updateToggleText();

      // Analytics: personalization applied
      if (window.__doubloon_track) {
        window.__doubloon_track('Personalization Applied', {
          product_title: this.getProductTitle(),
          product_id: this.getProductId(),
          product_url: window.location.href,
          engraving_text_length: this.engravingText.trim().length,
          font: this.selectedFont,
          cerakote_color: this.cerakoteColor || 'none',
          personalization_type: this.cerakoteColor ? 'Text & Cerakote' : 'Text Engraving',
          personalization_value: this.cerakoteColor ? 60 : 25,
        });
      }
    }

    updateApplyButton() {
      if (!this.applyBtn) return;

      // Enable apply button if text entered + confirmation checked
      const canApply =
        this.engravingText.trim().length > 0 && this.confirmed;
      this.applyBtn.disabled = !canApply;
    }

    updateToggleText() {
      if (!this.toggleBtn) return;

      const toggleText = this.toggleBtn.querySelector('.doubloon-personalizer__toggle-text strong');
      if (!toggleText) return;

      if (this.isApplied && !this.isOpen) {
        toggleText.textContent = 'Edit Personalization';
      } else {
        toggleText.textContent = 'Add Personalization';
      }
    }


    // ── Text Input ────────────────────────────────────────────

    onTextChange() {
      this.engravingText = this.textInput.value;
      const len = this.engravingText.length;
      if (this.charCount) {
        this.charCount.textContent = `${len} / 45 characters`;
      }

      // Update preview
      if (this.previewText) {
        this.previewText.textContent = this.engravingText;
      }

      // Reset applied state when text changes
      if (this.isApplied) {
        this.isApplied = false;
        if (this.savedMessage) this.savedMessage.hidden = true;
      }

      this.syncFormInputs();
      this.updateApplyButton();
    }

    // ── Font Selection ────────────────────────────────────────

    onFontChange(font) {
      this.selectedFont = font;

      // Update preview font
      if (this.previewText) {
        const fontMap = {
          'Arial': 'Arial, sans-serif',
          'Times New Roman': "'Times New Roman', serif",
          'Lucida Script': "'Lucida Handwriting', 'Lucida Calligraphy', cursive",
        };
        this.previewText.style.fontFamily = fontMap[font] || 'Arial, sans-serif';
      }

      // Reset applied state when font changes
      if (this.isApplied) {
        this.isApplied = false;
        if (this.savedMessage) this.savedMessage.hidden = true;
      }

      this.syncFormInputs();
      this.updateApplyButton();
    }

    // ── Cerakote Selection ────────────────────────────────────

    // Approximate hex values for cerakote preview
    static CERAKOTE_HEX = {
      'Polar Blue': '#7BB3D9',
      'Northern Lights': '#3CB371',
      'Blue Titanium': '#4A6D8C',
      'Crushed Orchid': '#9B59B6',
      'Dark Grey': '#4A4A4A',
      'Gun Metal Grey': '#6B6B6B',
      'Crushed Silver': '#C0C0C0',
      'Bright Nickel': '#D4D4D4',
      'Metallic': '#8C8C8C',
      'Burnt Bronze': '#8B6914',
      'Squatch': '#6B4423',
      'Gold': '#C4A35A',
      'Metallic Yellow': '#D4AA00',
      'Tequila Sunrise': '#FF6B35',
      'Rose Gold': '#B76E79',
      'Pink Champagne': '#F0C0C0',
      'Habanero Red': '#CC2936',
    };

    onCerakoteChange(color) {
      this.cerakoteColor = color;

      // Show/hide custom color input
      if (this.customColorInput) {
        this.customColorInput.hidden = color !== 'Your Color';
      }

      // Show/hide cerakote price row
      if (this.priceCerakoteRow) {
        this.priceCerakoteRow.hidden = !color;
      }
      if (this.priceCerakoteName) {
        this.priceCerakoteName.textContent = color === 'Your Color' ? 'Custom' : color;
      }

      // Update preview text color to match cerakote selection
      if (this.previewText) {
        const hex = DoubloonPersonalizer.CERAKOTE_HEX[color];
        this.previewText.style.color = hex || '';
      }

      // Reset applied state when cerakote changes
      if (this.isApplied) {
        this.isApplied = false;
        if (this.savedMessage) this.savedMessage.hidden = true;
      }

      this.updatePriceBreakdown();
      this.resolvePersonalizationVariant();
      this.syncFormInputs();
      this.updateApplyButton();
    }

    // ── Price Breakdown ───────────────────────────────────────

    updatePriceBreakdown() {
      if (!this.currentBaseVariant) return;

      const basePrice = this.getBasePrice();

      if (this.priceBase) {
        this.priceBase.textContent = this.formatMoney(basePrice);
      }

      const hasCerakote = !!this.cerakoteColor;
      const surcharge = hasCerakote ? this.TOTAL_TEXT_CERAKOTE : this.ENGRAVING_SURCHARGE;
      const total = basePrice + surcharge;

      if (this.priceTotal) {
        this.priceTotal.textContent = this.formatMoney(total);
      }
    }

    getBasePrice() {
      if (!this.currentBaseVariant) return 0;

      const variant = this.currentBaseVariant;
      const title = variant.title || '';

      // Find the "No Personalization" variant with the same base options
      const baseVariant = this.allVariants.find((v) => {
        const vTitle = v.title || '';
        return (
          vTitle.includes('No Personalization') &&
          this.sharesSameBaseOptions(vTitle, title)
        );
      });

      if (baseVariant) return baseVariant.price;

      // Fallback: use current variant price (no personalization variants exist)
      return variant.price;
    }

    sharesSameBaseOptions(titleA, titleB) {
      const PERSONALIZATION_OPTIONS = ['No Personalization', 'Text Engraving', 'Text & Cerakote'];
      const clean = (t) =>
        t
          .split('/')
          .map((part) => part.trim())
          .filter((part) => !PERSONALIZATION_OPTIONS.includes(part))
          .join(' / ');
      return clean(titleA) === clean(titleB);
    }

    // ── Variant Resolution ────────────────────────────────────

    resolvePersonalizationVariant() {
      if (!this.hasPersonalizationVariants) return;

      const personalizationType = this.cerakoteColor
        ? 'Text & Cerakote'
        : 'Text Engraving';

      this.selectPersonalizationOption(personalizationType);
    }

    /**
     * Select a personalization option by directly updating the form's variant ID.
     * Does NOT dispatch change events on Dawn's variant picker — this prevents
     * Dawn's section re-render that would destroy our DOM state (cursor, text,
     * scroll position).
     */
    selectPersonalizationOption(optionValue) {
      // Find the target variant matching this personalization + current base options
      const targetVariant = this.allVariants.find((v) => {
        const title = v.title || '';
        if (!title.includes(optionValue)) return false;
        if (!this.currentBaseVariant) return true;
        return this.sharesSameBaseOptions(title, this.currentBaseVariant.title || '');
      });

      if (!targetVariant) return;

      const productInfo = document.getElementById(`MainProduct-${this.sectionId}`);
      if (!productInfo) return;

      // Silently update the form's variant ID — NO event dispatch
      const variantInput = productInfo.querySelector('.product-variant-id');
      if (variantInput) {
        variantInput.value = targetVariant.id;
      }

      // Silently update the native picker state for form consistency
      const variantSelects = productInfo.querySelector('variant-selects');
      if (variantSelects) {
        const inputs = variantSelects.querySelectorAll('input[type="radio"], select option');
        for (const input of inputs) {
          if (input.tagName === 'OPTION') {
            if (input.value === optionValue) input.selected = true;
          } else if (input.value === optionValue) {
            input.checked = true;
          }
        }
      }

      // Update Dawn's displayed price without triggering re-render
      this.updateDisplayedPrice(targetVariant);

      // Update media gallery if the target variant has a different featured image
      if (targetVariant.featured_media) {
        const mediaGallery = productInfo.querySelector('media-gallery');
        if (mediaGallery) {
          mediaGallery.setActiveMedia?.(
            `${this.sectionId}-${targetVariant.featured_media.id}`,
            true
          );
        }
      }

      // Update URL for sharing/bookmarks
      const url = new URL(window.location.href);
      url.searchParams.set('variant', targetVariant.id);
      window.history.replaceState({}, '', url.toString());

      // Keep our own price breakdown in sync
      this.updatePriceBreakdown();
    }

    /**
     * Update Dawn's price display elements with a variant's price.
     * Called instead of Dawn's own re-render.
     */
    updateDisplayedPrice(variant) {
      const productInfo = document.getElementById(`MainProduct-${this.sectionId}`);
      if (!productInfo) return;

      const priceContainer = productInfo.querySelector('.price');
      if (!priceContainer) return;

      const regularPrice = priceContainer.querySelector('.price-item--regular');
      if (regularPrice) {
        regularPrice.innerHTML = this.formatMoney(variant.price);
      }

      const salePrice = priceContainer.querySelector('.price-item--sale');
      if (salePrice) {
        salePrice.innerHTML = this.formatMoney(variant.price);
      }
    }

    // ── Form Sync ─────────────────────────────────────────────

    syncFormInputs() {
      if (this.isOpen && this.engravingText) {
        if (this.propText) this.propText.value = this.engravingText;
        if (this.propFont) this.propFont.value = this.selectedFont;
        if (this.propCerakote) {
          if (this.cerakoteColor === 'Your Color') {
            this.propCerakote.value = `Custom: ${this.customColorText}`;
          } else {
            this.propCerakote.value = this.cerakoteColor;
          }
        }
      } else {
        // Clear properties when personalizer is closed or no text entered
        if (this.propText) this.propText.value = '';
        if (this.propFont) this.propFont.value = '';
        if (this.propCerakote) this.propCerakote.value = '';
      }

      // Disable empty properties so they don't appear in cart
      [this.propText, this.propFont, this.propCerakote].forEach((input) => {
        if (input) input.disabled = !input.value;
      });
    }

    // ── Submit Button State ───────────────────────────────────

    updateSubmitButton() {
      const productInfo = document.getElementById(`MainProduct-${this.sectionId}`);
      if (!productInfo) return;

      const submitBtn = productInfo.querySelector('.product-form__submit');
      if (!submitBtn) return;

      if (this.isOpen) {
        // When panel is open, disable submit - require Apply first
        submitBtn.disabled = !this.isApplied;
      } else if (this.isApplied) {
        // Panel closed but personalization applied - enable submit
        submitBtn.disabled = false;
      }
      // Otherwise, Dawn's own logic manages the submit button state

      // Notify sticky ATC of submit button state change
      document.dispatchEvent(
        new CustomEvent('personalizer-submit-state', {
          detail: { disabled: submitBtn.disabled },
        })
      );
    }

    /**
     * Restore Dawn's default submit button state when personalizer closes.
     * Dawn manages this based on variant availability — we just need to
     * re-enable it so Dawn can take over.
     */
    restoreSubmitButton() {
      const productInfo = document.getElementById(`MainProduct-${this.sectionId}`);
      if (!productInfo) return;

      const submitBtn = productInfo.querySelector('.product-form__submit');
      if (!submitBtn) return;

      // Re-enable — Dawn's variant change handler will correct if needed
      submitBtn.disabled = false;
    }

    // ── Helpers ───────────────────────────────────────────────

    formatMoney(cents) {
      if (window.Shopify?.formatMoney) {
        return window.Shopify.formatMoney(cents, window.theme?.moneyFormat);
      }
      return '$' + (cents / 100).toFixed(2);
    }

    getProductTitle() {
      var analytics = window.__doubloon_analytics;
      return (analytics && analytics.product && analytics.product.title) || document.title;
    }

    getProductId() {
      var analytics = window.__doubloon_analytics;
      return (analytics && analytics.product && analytics.product.id) || '';
    }
  }

  customElements.define('doubloon-personalizer', DoubloonPersonalizer);
}
